import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Share2,
  Clock
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import TagPill from '../components/TagPill';
import GradientButton from '../components/GradientButton';
import { useEpisodes, getEpisodeById } from '../hooks/useEpisodes';
import podcastsData from '../data/podcasts.json';

// Custom Audio Player Component
const AudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipBack = () => {
    const audio = audioRef.current;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    audio.currentTime = Math.min(duration, audio.currentTime + 30);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-[var(--bg-secondary)] rounded-2xl p-6 border border-[var(--bg-tertiary)]"
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Progress Bar */}
      <div
        className="relative h-2 bg-[var(--bg-tertiary)] rounded-full cursor-pointer mb-6 group"
        onClick={handleSeek}
      >
        {/* Progress fill */}
        <div
          className="absolute left-0 top-0 h-full bg-gradient-primary rounded-full transition-all"
          style={{ width: `${progressPercent}%` }}
        />
        {/* Progress handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg
                     opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progressPercent}% - 8px)` }}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        {/* Time Display */}
        <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm font-mono min-w-[100px]">
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-4">
          {/* Skip Back */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={skipBack}
            className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label="Skip back 10 seconds"
          >
            <div className="relative">
              <SkipBack size={24} />
              <span className="absolute -bottom-1 -right-1 text-[10px] font-bold">10</span>
            </div>
          </motion.button>

          {/* Play/Pause */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={togglePlay}
            className={`flex items-center justify-center w-14 h-14 rounded-full bg-gradient-primary
                       text-white shadow-glow hover:shadow-glow-lg transition-all ${isPlaying ? 'glow-pulse' : ''}`}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause size={24} fill="currentColor" />
            ) : (
              <Play size={24} fill="currentColor" className="ml-1" />
            )}
          </motion.button>

          {/* Skip Forward */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={skipForward}
            className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label="Skip forward 30 seconds"
          >
            <div className="relative">
              <SkipForward size={24} />
              <span className="absolute -bottom-1 -right-1 text-[10px] font-bold">30</span>
            </div>
          </motion.button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center gap-2 min-w-[100px] justify-end">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleMute}
            className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </motion.button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-20 h-1 bg-[var(--bg-tertiary)] rounded-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3
                       [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-accent
                       [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
          />
        </div>
      </div>
    </motion.div>
  );
};

// Platform Button Component
const PlatformButton = ({ platform, url, icon, label, color }) => (
  <motion.a
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex items-center gap-3 px-5 py-3 rounded-xl ${color} transition-all duration-200`}
  >
    <i className={`${icon} text-lg`} />
    <span className="font-medium">{label}</span>
  </motion.a>
);

const EpisodePage = () => {
  const { id } = useParams();
  const { episodes, loading, error } = useEpisodes();
  const { socialMedia } = podcastsData;

  const episode = getEpisodeById(episodes, id);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-4 bg-[var(--bg-tertiary)] rounded w-32" />
            <div className="h-8 bg-[var(--bg-tertiary)] rounded w-3/4" />
            <div className="h-6 bg-[var(--bg-tertiary)] rounded w-1/2" />
            <div className="h-32 bg-[var(--bg-tertiary)] rounded" />
            <div className="h-48 bg-[var(--bg-tertiary)] rounded" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error/Not Found State
  if (error || !episode) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <h1 className="font-display text-display-md text-[var(--text-primary)] mb-4">
              Episode Not Found
            </h1>
            <p className="text-[var(--text-secondary)] mb-8">
              Sorry, we couldn't find the episode you're looking for.
            </p>
            <GradientButton to="/episodes" icon={<ArrowLeft size={18} />} iconPosition="left">
              Back to Episodes
            </GradientButton>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  // Truncate description for meta tags
  const metaDescription = episode.description
    ? episode.description.substring(0, 160) + (episode.description.length > 160 ? '...' : '')
    : 'Listen to this episode of News From The Pew podcast.';

  return (
    <>
      <Helmet>
        <title>{episode.title} | News From The Pew</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://newsfromthepew.com/episode/${episode.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://newsfromthepew.com/episode/${episode.id}`} />
        <meta property="og:title" content={`${episode.title} | News From The Pew`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={episode.thumbnail || 'https://newsfromthepew.com/assets/images/default_branding_image.png'} />
        <meta property="og:audio" content={episode.audio} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${episode.title} | News From The Pew`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={episode.thumbnail || 'https://newsfromthepew.com/assets/images/default_branding_image.png'} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "PodcastEpisode",
            "name": episode.title,
            "description": episode.description,
            "datePublished": episode.date,
            "url": `https://newsfromthepew.com/episode/${episode.id}`,
            "image": episode.thumbnail || 'https://newsfromthepew.com/assets/images/default_branding_image.png',
            "audio": {
              "@type": "AudioObject",
              "contentUrl": episode.audio
            },
            "partOfSeries": {
              "@type": "PodcastSeries",
              "name": "News From The Pew"
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />

        <main>
        {/* Header Section */}
        <section className="relative py-12 overflow-hidden">
          {/* Ambient glows */}
          <div className="ambient-orb-primary -top-20 -left-32 w-80 h-80" />
          <div className="ambient-orb-secondary -bottom-20 -right-20 w-64 h-64" />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/episodes"
                className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-accent
                           transition-colors mb-8 group"
              >
                <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                <span>Back to Episodes</span>
              </Link>
            </motion.div>

            {/* Episode Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              {/* Episode Artwork */}
              {episode.thumbnail && (
                <div className="mb-6">
                  <div className="relative w-full max-w-md mx-auto md:mx-0 aspect-square rounded-2xl overflow-hidden shadow-card">
                    <img
                      src={episode.thumbnail}
                      alt={episode.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  </div>
                </div>
              )}

              {/* Episode Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                {episode.episodeNumber && (
                  <span className="text-sm font-semibold text-accent-warm uppercase tracking-wider">
                    Episode {episode.episodeNumber}
                  </span>
                )}
                <span className="text-[var(--text-muted)] text-sm">•</span>
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                  <Clock size={14} />
                  <span>{episode.date}</span>
                </div>
                {episode.duration && (
                  <>
                    <span className="text-[var(--text-muted)] text-sm">•</span>
                    <span className="text-[var(--text-muted)] text-sm">{episode.duration}</span>
                  </>
                )}
              </div>

              {/* Title */}
              <h1 className="font-display text-display-sm md:text-display-md lg:text-display-lg text-[var(--text-primary)] mb-6 leading-tight">
                {episode.title}
              </h1>

              {/* Gradient Divider */}
              <div className="h-1 w-32 bg-gradient-primary rounded-full" />
            </motion.header>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Audio Player */}
            <div className="mb-8">
              <AudioPlayer src={episode.audio} title={episode.title} />
            </div>

            {/* Tags */}
            {episode.tags && episode.tags.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-8"
              >
                <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-3">Topics</p>
                <div className="flex flex-wrap gap-2">
                  {episode.tags.map(tag => (
                    <TagPill key={tag} tag={tag} size="default" />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-12"
            >
              <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-3">About This Episode</p>
              {episode.descriptionHtml ? (
                <div
                  className="episode-description text-[var(--text-secondary)] text-lg leading-relaxed
                             [&>p]:mb-4 [&>p:last-child]:mb-0
                             [&_strong]:text-[var(--text-primary)] [&_strong]:font-semibold
                             [&_em]:italic
                             [&_a]:text-accent [&_a]:underline [&_a:hover]:text-accent-hover
                             [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc
                             [&_li]:mb-2
                             [&_br]:block [&_br]:mb-2"
                  dangerouslySetInnerHTML={{ __html: episode.descriptionHtml }}
                />
              ) : (
                <p className="text-[var(--text-secondary)] text-lg leading-relaxed whitespace-pre-line">
                  {episode.description}
                </p>
              )}
            </motion.div>

            {/* Listen On Platforms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mb-12"
            >
              <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-4">Listen On</p>
              <div className="flex flex-wrap gap-3">
                {socialMedia.applePodcasts && (
                  <PlatformButton
                    platform="apple"
                    url={socialMedia.applePodcasts}
                    icon="fab fa-apple"
                    label="Apple Podcasts"
                    color="bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)] text-[var(--text-primary)]"
                  />
                )}
                {socialMedia.spotify && (
                  <PlatformButton
                    platform="spotify"
                    url={socialMedia.spotify}
                    icon="fab fa-spotify"
                    label="Spotify"
                    color="bg-[#1DB954] hover:bg-[#1ed760] text-white"
                  />
                )}
                {socialMedia.amazonMusic && (
                  <PlatformButton
                    platform="amazon"
                    url={socialMedia.amazonMusic}
                    icon="fab fa-amazon"
                    label="Amazon Music"
                    color="bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)] text-[var(--text-primary)]"
                  />
                )}
              </div>
            </motion.div>

            {/* Share Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="border-t border-[var(--bg-tertiary)] pt-8"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[var(--text-secondary)]">
                  Enjoyed this episode? Share it with others!
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: episode.title,
                        text: episode.description,
                        url: window.location.href,
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)]
                             text-[var(--text-primary)] rounded-xl transition-colors"
                >
                  <Share2 size={18} />
                  <span>Share Episode</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
  );
};

export default EpisodePage;
